import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ScrollReveal from '../shared/ScrollReveal'
import GradientText from '../shared/GradientText'
import { Icons } from '../shared/Icons'
import './MLSection.css'

const mlModels = [
  {
    id: 'anomaly',
    name: 'Anomaly Detector',
    type: 'Isolation Forest',
    category: 'Real-time streaming',
    description: 'Watches 30+ live signals. Flags subtle performance shifts before they become faults.',
    accuracy: '93%',
    badge: 'REAL-TIME STREAMING',
    icon: Icons.bolt
  },
  {
    id: 'fault',
    name: 'Fault Classifier',
    type: 'XGBoost Multi-Class',
    category: 'Diagnostics',
    description: 'Pinpoints root cause across 16 fault categories — from PID to shading to series resistance.',
    accuracy: '96%',
    badge: '16 FAULT TYPES',
    icon: Icons.target
  },
  {
    id: 'failure',
    name: 'Failure Predictor',
    type: 'Random Forest + Survival',
    category: 'Predictive',
    description: 'Forecasts component failures and remaining useful life via multi-modal data fusion.',
    accuracy: '91%',
    badge: '30–180 DAYS AHEAD',
    icon: Icons.activity
  },
  {
    id: 'forecaster',
    name: 'Generation Forecaster',
    type: 'Physics + LSTM',
    category: 'Time series',
    description: 'Fuses physics-based PV model with neural networks for power output predictions.',
    accuracy: '94%',
    badge: '15 MIN → 12 MONTHS',
    icon: Icons.trending
  },
  {
    id: 'drone',
    name: 'Drone Defect Detector',
    type: 'YOLOv8 Thermal',
    category: 'Computer vision',
    description: 'Scans FLIR imagery for hotspots, cracks, micro-fractures, and soiling — 9 defect types.',
    accuracy: '98%',
    badge: '<200MS PER IMAGE',
    icon: Icons.drone
  },
  {
    id: 'trade',
    name: 'Trade Optimizer',
    type: 'Coming soon',
    category: 'Optimization',
    description: 'Dynamic electricity-market trading — sell when prices peak, store when they dip.',
    accuracy: 'Soon',
    badge: 'COMING SOON',
    icon: Icons.battery,
    comingSoon: true
  }
]

export default function MLSection() {
  const [activeModel, setActiveModel] = useState(0)
  const current = mlModels[activeModel]
  const accuracyWidth = current.comingSoon
    ? '0%'
    : current.accuracy === 'Optimal'
      ? '100%'
      : current.accuracy

  return (
    <section className="ml-section" id="ml">
      <div className="container">
        <ScrollReveal className="ml-header">
          <span className="ml-badge">// THE PREDICTIVE BRAIN</span>
          <h2 className="ml-title">
            Five ML models.{' '}
            <GradientText gradient="space">One intelligence.</GradientText>
          </h2>
          <p className="ml-subtitle">
            Purpose-built machine learning for solar operations. False alarms cut by 80% — and issues that would otherwise go undetected, surfaced before it's too late.
          </p>
        </ScrollReveal>

        <div className="ml-grid">
          {/* Model selector */}
          <ScrollReveal animation="fadeLeft" className="ml-selector">
            {mlModels.map((model, i) => (
              <motion.button
                key={model.id}
                className={`ml-model-btn ${activeModel === i ? 'active' : ''} ${model.comingSoon ? 'coming-soon' : ''}`}
                onClick={() => setActiveModel(i)}
                whileHover={{ x: 5 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="ml-model-icon">
                  <model.icon size={20} />
                </span>
                <div className="ml-model-info">
                  <span className="ml-model-name">{model.name}</span>
                  <span className="ml-model-type">{model.type}</span>
                </div>
                <span className="ml-model-accuracy">{model.accuracy}</span>
              </motion.button>
            ))}
          </ScrollReveal>

          {/* Model details */}
          <ScrollReveal animation="fadeRight" className="ml-details">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeModel}
                className="ml-detail-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="ml-detail-header">
                  <span className="ml-detail-icon">
                    {(() => {
                      const IconComponent = current.icon
                      return <IconComponent size={28} />
                    })()}
                  </span>
                  <div>
                    <h3>{current.name}</h3>
                    <span className="ml-detail-category">{current.category}</span>
                  </div>
                </div>

                <div className="ml-detail-type">
                  <span className="type-label">Model Type</span>
                  <span className="type-value">{current.type}</span>
                </div>

                <p className="ml-detail-desc">{current.description}</p>

                <div className="ml-detail-accuracy">
                  <span className="accuracy-label">{current.comingSoon ? 'Status' : 'Accuracy'}</span>
                  <div className="accuracy-bar">
                    <motion.div
                      className="accuracy-fill"
                      initial={{ width: 0 }}
                      animate={{ width: accuracyWidth }}
                      transition={{ duration: 0.8, ease: 'easeOut' }}
                    />
                  </div>
                  <span className="accuracy-value">{current.badge}</span>
                </div>
              </motion.div>
            </AnimatePresence>
          </ScrollReveal>
        </div>

        <ScrollReveal delay={0.4} className="ml-footer-strip">
          <span className="ml-footer-item">
            <span className="ml-footer-dot ml-footer-dot-green" /> CONTINUOUSLY TRAINING
          </span>
          <span className="ml-footer-item">FALSE ALARMS −80%</span>
          <span className="ml-footer-item">ISSUES THAT OTHERWISE GO UNDETECTED</span>
        </ScrollReveal>
      </div>
    </section>
  )
}
